import { Injectable } from '@angular/core';
import { AuthConstants } from 'src/app/config/auth-constants';
import { getDateIso } from 'src/app/utils/date';
import { HttpService } from '../http/http.service';
import { StorageService } from '../storage/storage.service';
import { ToastService } from '../toast/toast.service';
import { AuthService } from './../auth/auth.service';
import { Pix } from './../transaction/modal/pix';
import { User } from './model/user';
import { UserIndicateds } from './model/user-indicateds';
import { UserStatements } from './model/user-statements';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(
    private authService: AuthService,
    private httpService: HttpService,
    private storageService: StorageService,
    private toastService: ToastService
  ) {}

  getUser(): Promise<User> {
    const user: User = new User();
    return new Promise((resolve, reject) => {
      this.httpService.get(`profile/user/`).subscribe({
        next: (res: any) => {
          if (res) {
            user.fromJson(res);
            this.storageService.store(AuthConstants.AUTH, user);
            resolve(user);
          }
        },
        error: (error) => {
          const msg =
            error?.error?.msg ||
            error?.error?.message ||
            'Ocorreu um erro ao buscar seus dados!';
          this.toastService.presentToastError(msg);
          this.authService.logout(false);
          reject(msg);
        },
      });
    });
  }

  update(user: User): Promise<any> {
    const address = user?.address?.zipCode ? user?.address : null;
    const city = user?.address?.city?.country ? user?.address?.city : null;
    const bankAccount = user?.bankAccount?.bankId ? user?.bankAccount : null;

    user.document = user.document?.trim().replace(/\D/g, '');
    user.phone = user.phone?.trim().replace(/\D/g, '');

    let birth = getDateIso(user.birth);
    birth = new Date(birth).toISOString();
    user.birth = birth;

    return new Promise((resolve, reject) => {
      this.httpService
        .put('profile/user', {
          user,
          address,
          city,
          bankAccount,
        })
        .subscribe({
          next: (res: any) => {
            resolve(res);
          },
          error: (error) => {
            const msg =
              error?.error?.msg ||
              error?.error?.error ||
              'Ocorreu um erro ao tentar atualizar seus dados.';
            reject(msg);
          },
        });
    });
  }

  getUserByDocument(document: string): Promise<User> {
    const user: User = new User();
    return new Promise((resolve, reject) => {
      this.httpService.get(`profile/by-document/${document}/`).subscribe({
        next: (res: any) => {
          if (res) {
            user.fromJson(res);
            resolve(user);
          }
        },
        error: (error) => {
          const msg =
            error?.error?.msg ||
            error?.error?.message ||
            'Ocorreu um erro ao buscar o usuário pelo documento!';
          reject(msg);
        },
      });
    });
  }

  getUserByEmail(email: string): Promise<User> {
    const user: User = new User();
    return new Promise((resolve, reject) => {
      this.httpService.get(`profile/by-email/${email}/`).subscribe({
        next: (res: any) => {
          if (res) {
            user.fromJson(res);
            resolve(user);
          }
        },
        error: (error) => {
          const msg =
            error?.error?.msg ||
            error?.error?.message ||
            'Ocorreu um erro ao buscar o usuário pelo e-mail!';
          reject(msg);
        },
      });
    });
  }

  getIndicateds(): Promise<UserIndicateds> {
    const ui: UserIndicateds = new UserIndicateds();
    return new Promise((resolve, reject) => {
      this.httpService.get(`profile/indication-report/`).subscribe({
        next: (res: any) => {
          if (res) {
            resolve(Object.assign(new UserIndicateds(), res));
          }
        },
        error: (error) => {
          const msg =
            error?.error?.msg ||
            error?.error?.message ||
            'Ocorreu um erro ao buscar seus indicados!';
          this.toastService.presentToastError(msg);
          reject(msg);
        },
      });
    });
  }

  getIndicatedsList(): Promise<UserIndicateds> {
    return new Promise((resolve, reject) => {
      this.httpService.get(`profile/indication-report-list/`).subscribe({
        next: (res: any) => {
          if (res) {
            let lsUsers: User[] = [];
            const ui: UserIndicateds = Object.assign(new UserIndicateds(), res);
            ui?.indicatedCompanies?.forEach((ic) => {
              lsUsers.push(Object.assign(new User(), ic));
            });
            ui.indicatedCompanies = lsUsers;
            lsUsers = [];
            ui?.indicatedUsers?.forEach((iu) => {
              lsUsers.push(Object.assign(new User(), iu));
            });

            ui.indicatedUsers = lsUsers;
            resolve(ui);
          }
        },
        error: (error) => {
          const msg =
            error?.error?.msg ||
            error?.error?.message ||
            'Ocorreu um erro ao buscar seus indicados!';
          this.toastService.presentToastError(msg);
          reject(msg);
        },
      });
    });
  }

  getStatements(): Promise<UserStatements> {
    return new Promise((resolve, reject) => {
      this.httpService.get(`profile/transfer-report/`).subscribe({
        next: (res: any) => {
          if (res) {
            const statement = Object.assign(new UserStatements(), res);
            statement.totalAmount = statement.totalAmount / 100;
            statement.totalCashbackAmount = statement.totalCashbackAmount / 100;
            statement.indicatorCredits = statement.indicatorCredits / 100;
            statement.indicatorCreditsOnHold =
              statement.indicatorCreditsOnHold / 100;
            resolve(statement);
          }
        },
        error: (error) => {
          const msg =
            error?.error?.msg ||
            error?.error?.message ||
            'Ocorreu um erro ao buscar seu saldo!';
          this.toastService.presentToastError(msg);
          reject(msg);
        },
      });
    });
  }

  goPremium(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.httpService.post(`profile/go-premium/`, null).subscribe({
        next: (res: any) => {
          if (res) {
            resolve(true);
          }
        },
        error: (error) => {
          const msg =
            error?.error?.msg ||
            error?.error?.message ||
            'Ocorreu um erro ao alterar seu plano para Premium!';
          this.toastService.presentToastError(msg);
          reject(msg);
        },
      });
    });
  }

  getQRCode(value: number, uuid: string): Promise<string> {
    return new Promise((resolve, reject) => {
      this.httpService
        .get(
          `profile/receive-qrcode/${(value * 100).toFixed(
            0
          )}/${encodeURIComponent(uuid)}/`
        )
        .subscribe({
          next: (res: any) => {
            if (res) {
              resolve(res);
            }
          },
          error: (error) => {
            const msg =
              error?.error?.msg ||
              error?.error?.message ||
              'Ocorreu um erro ao gerar seu QRCode!';
            this.toastService.presentToastError(msg);
            reject(msg);
          },
        });
    });
  }

  getQRCodePayToMeWithTxid(value: number, document: string): Promise<Pix> {
    return new Promise((resolve, reject) => {
      this.httpService
        .get(
          `profile/qrcode-payment-to-me-with-txid/${document}/${(
            value * 100
          ).toFixed(0)}`
        )
        .subscribe({
          next: (res: any) => {
            if (res?.base64QrCode) {
              resolve(Object.assign(new Pix(), res));
            }
          },
          error: (error) => {
            const msg =
              error?.error?.msg ||
              error?.error?.message ||
              'Ocorreu um erro ao gerar seu QRCode!';
            this.toastService.presentToastError(msg);
            reject(msg);
          },
        });
    });
  }

  getQRCodeTotemPayToMe(value: number, document: string): Promise<string> {
    return new Promise((resolve, reject) => {
      this.httpService
        .get(
          `profile/totem-qrcode-payment-to-me/${(value * 100).toFixed(
            0
          )}/${document}`
        )
        .subscribe({
          next: (res: any) => {
            if (res?.uuid) {
              resolve(res.uuid);
            }
          },
          error: (error) => {
            const msg =
              error?.error?.msg ||
              error?.error?.message ||
              'Ocorreu um erro ao solicitar seu pagamento.';
            this.toastService.presentToastError(msg);
            reject(msg);
          },
        });
    });
  }

  existingUser(key: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.httpService.get(`macros/existing-user/${key}`).subscribe({
        next: (res: any) => {
          if (res) {
            resolve(true);
          } else {
            reject(false);
          }
        },
        error: (error) => {
          reject(false);
        },
      });
    });
  }

  getIndicationQRCode(): Promise<string> {
    return new Promise((resolve, reject) => {
      this.httpService.get(`profile/indication-qrcode/`).subscribe({
        next: (res: any) => {
          if (res?.qrCodeBase64) {
            resolve(res?.qrCodeBase64);
          }
        },
        error: (error) => {
          const msg =
            error?.error?.msg ||
            error?.error?.message ||
            'Ocorreu um erro ao gerar seu QRCode!';
          reject(msg);
        },
      });
    });
  }

  changeToSelfEmployedCompany(postData: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.httpService
        .post(`profile/company/self-employed-company/`, postData)
        .subscribe({
          next: (res: any) => {
            if (res) {
              resolve(true);
            }
          },
          error: (error) => {
            const msg =
              error?.error?.msg ||
              error?.error?.message ||
              'Ocorreu um erro ao alterar para Modo Negócios!';
            reject(msg);
          },
        });
    });
  }

  deactivateSelfEmployedCompany(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.httpService
        .delete(`profile/company/self-employed-company/`)
        .subscribe({
          next: (res: any) => {
            if (res) {
              resolve(true);
            }
          },
          error: (error) => {
            const msg =
              error?.error?.msg ||
              error?.error?.message ||
              'Ocorreu um erro ao desativar o Modo Negócios!';
            reject(msg);
          },
        });
    });
  }

  setPasswordTransaction(password: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.httpService
        .post(`profile/password-transaction/`, { password })
        .subscribe({
          next: (res: any) => {
            if (res) {
              resolve(true);
            }
          },
          error: (error) => {
            const msg =
              error?.error?.msg ||
              error?.error?.message ||
              'Ocorreu um erro ao cadastrar sua senha para transações!';
            reject(msg);
          },
        });
    });
  }

  updatePasswordTransaction(
    passwordTransaction: string,
    passwordApp: string
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.httpService
        .put(`profile/password-transaction/`, {
          passwordTransaction,
          passwordApp,
        })
        .subscribe({
          next: (res: any) => {
            if (res) {
              resolve(true);
            }
          },
          error: (error) => {
            const msg =
              error?.error?.msg ||
              error?.error?.message ||
              'Ocorreu um erro ao alterar sua senha para transações!';
            reject(msg);
          },
        });
    });
  }

  activateAccount(email: string, code: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.httpService.get(`user/pin/${email}/${code}/`).subscribe({
        next: () => {
          this.storageService.removeStorageItem(AuthConstants.ACTIVATION_EMAIL);
          resolve(true);
        },
        error: (error) => {
          const msg =
            error?.error?.msg ||
            error?.error?.message ||
            'Ocorreu um erro ao ativar sua conta!';
          reject(msg);
        },
      });
    });
  }
}
